import { useMemo } from "react";
import { formatValue, toneColor } from "../../lib/kernelFormat.js";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { KernelEmpty, useLinkLabels, linkLabel } from "./shared.jsx";

export const kind = "rows";

export function Component({ spec, data, onOpenScope }) {
    const rows = useMemo(() => {
        const arr = Array.isArray(data) ? data : [];
        return arr.slice(0, spec.maxRows || 20);
    }, [data, spec.maxRows]);

    const linkSpec = spec.link;
    useLinkLabels(rows, linkSpec);

    if (!rows.length) return <KernelEmpty message={spec.empty || "no rows"} />;

    return (
        <div className="space-y-[2px]">
            {rows.map((row, i) => {
                const primary = linkLabel(linkSpec, row, formatValue(row?.[spec.primaryField], "text"));
                const secondary = spec.secondaryField ? formatValue(row?.[spec.secondaryField], "text") : null;
                const right = spec.rightField ? formatValue(row?.[spec.rightField], spec.rightFormat || "number") : null;
                const rightStyle = spec.rightTone ? { color: toneColor(spec.rightTone) } : undefined;
                const id = linkSpec?.idField ? row?.[linkSpec.idField] : null;
                const handler = id != null && onOpenScope ? () => onOpenScope({
                    dim: linkSpec.dim,
                    ids: [String(id)],
                    label: linkSpec.labelField ? row?.[linkSpec.labelField] : primary,
                }) : null;
                // `wrap` keeps the whole primary text on screen, wrapping
                // over lines, for rows that are sentences rather than names;
                // nothing overflows, so no hover tooltip is mounted.
                const wrap = spec.wrap === true;
                const PrimaryTag = handler ? "button" : "div";
                return (
                    <div
                        key={i}
                        className={`flex ${wrap ? "items-start" : "items-baseline"} gap-[8px] py-[2px] border-b border-[color:var(--line-soft)] last:border-b-0 text-[11px] leading-[1.3]`}
                    >
                        {/* Button only when clickable — a DISABLED button
                            swallows mouseenter and would kill the hover
                            tooltip on non-linked labels. Non-clickable → div. */}
                        {wrap ? (
                            <PrimaryTag
                                {...(handler ? { type: "button", onClick: handler } : {})}
                                className={`min-w-0 flex-1 whitespace-normal break-words font-mono text-[color:var(--ink)] text-left${handler ? " cursor-pointer hover:underline" : ""}`}
                            >
                                {primary}
                            </PrimaryTag>
                        ) : (
                            <OverflowTooltip
                                as={PrimaryTag}
                                {...(handler ? { type: "button", onClick: handler } : {})}
                                text={String(primary)}
                                className={`min-w-0 flex-1 font-mono text-[color:var(--ink)] text-left${handler ? " cursor-pointer hover:underline" : ""}`}
                            >
                                {primary}
                            </OverflowTooltip>
                        )}
                        {secondary && wrap ? (
                            <span className="shrink-0 text-[color:var(--muted)]">{secondary}</span>
                        ) : secondary ? (
                            <OverflowTooltip as="span" text={String(secondary)} className="min-w-0 flex-1 text-[color:var(--muted)]">
                                {secondary}
                            </OverflowTooltip>
                        ) : null}
                        {right != null ? (
                            <span
                                className="shrink-0 whitespace-nowrap font-mono tabular-nums text-[10.5px] text-[color:var(--muted)]"
                                style={rightStyle}
                            >
                                {right}
                            </span>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}
